<template>
  <form @submit.prevent="submit">
    <FormMessage :errors="errors" v-if="errors" />
    <FormInput
      name="domain"
      v-model="fields.domain"
      :label="`${$t('global.domain')} *`"
      :errors="validation.fields.domain.errors"
      :dirty="validation.fields.domain.dirty"
    />
    <FormSelect
      name="type"
      v-model="fields.type"
      :label="`${$t('global.type')} *`"
      :errors="validation.fields.type.errors"
      :dirty="validation.fields.type.dirty"
    >
      <option value="" :selected="true">{{ $t("global.select-pointer-domain-type") }}</option>
      <option
        v-for="(domainType, index) in types"
        :key="index"
        :value="domainType"
        :selected="isSelected(domainType)"
      >
        {{ getDomainTypeName(domainType) }}
      </option>
    </FormSelect>
    <div class="form-buttons">
      <Btn
        class="mr-2"
        variant="light"
        type="button"
        :disabled="loading"
        @click="cancel"
        data-test="cancel-enable-elastic-search-button"
      >
        {{ $t("global.cancel") }}
      </Btn>
      <Btn
        variant="primary"
        type="submit"
        :disabled="!isSubmittable"
        :loading="loading"
        data-test="enable-elastic-search-button"
      >
        {{ $t("global.submit") }}
      </Btn>
    </div>
  </form>
</template>

<script>
import { reactive, ref } from "@vue/composition-api";
import addPointerDomainRules from "leatherman-js/utils/validations/forms/addPointerDomain";
import Btn from "@/components/display/Btn";
import FormInput from "@/components/display/form/FormInput";
import FormMessage from "@/components/display/form/FormMessage";
import FormSelect from "@/components/display/form/FormSelect";
import store from "@/store";
import useValidator from "@/utils/useValidator";

export default {
  name: "AddPointerDomainForm",
  components: {
    FormInput,
    FormMessage,
    FormSelect,
    Btn,
  },
  props: {
    site: {
      type: Object,
      required: true,
    },
    types: {
      type: Array,
      required: true,
    },
  },
  setup(props, { root, emit }) {
    const loading = ref(false);
    const errors = ref(null);
    const fields = reactive({
      domain: null,
      type: null,
    });

    const { validator } = useValidator();
    const validation = validator(
      fields,
      addPointerDomainRules,
      {},
      {
        domain: {
          domain: root.$t("validation.domain-invalid"),
          required: root.$t("validation.domain-required"),
        },
        type: {
          required: root.$t("validation.type-required"),
        },
      }
    );

    const isSelected = (domainType) => fields.type === domainType;
    const isSubmittable = () => fields.domain && fields.type;
    const getType = (domainType) => domainType.replace(/_/g, "-");

    const cancel = () => {
      emit("cancel");
    };

    const submit = () => {
      loading.value = true;
      errors.value = null;
      validation
        .validate()
        .then(() => {
          return store
            .dispatch("pointerDomains/create", {
              siteId: props.site.id,
              domain: fields.domain,
              domainType: fields.type,
            })
            .then(() => {
              store.dispatch("toasts/addToast", {
                variant: "sucess",
                message: root.$t("toasts.pointer-domain-creating-message", {
                  domain: fields.domain,
                }),
              });
              emit("success");
            })
            .catch(() => {
              store.dispatch("toasts/addToast", {
                variant: "error",
                message: root.$t("toasts.pointer-domain-creating-error-message", {
                  domain: fields.domain,
                }),
              });
            });
        })
        .finally(() => {
          loading.value = false;
        });
    };

    const getDomainTypeName = (domainType) => {
      return root.$t(`global.pointer-domain-type-${getType(domainType)}`);
    };

    return {
      fields,
      submit,
      cancel,
      errors,
      loading,
      getType,
      isSelected,
      validation,
      isSubmittable,
      getDomainTypeName,
    };
  },
};
</script>
